<template>
  <div>
    <WCSpinner v-if="!isFetched || isLoading" position="center" size="xl" />
    <template v-else>
      <WCEmptyCart ref="focusFirst" v-if="isEmpty && giftCardItems.length < 1" />
      <template v-else>
        <h1 tabindex="-1" ref="focusFirst" class="px-4">{{ $t('checkOut') }}</h1>
        <div class="row">
          <ul class="d-flex flex-column pl-lg-0 col-lg-9">
            <WCFinalizeStage
              id="stage1"
              :activeStage="checkoutStage"
              :instructions="orderInstructions"
              :lineItems="lineItems"
              :outofstock-line-items="getOutOfStockLineItemsOfType(1)"
              :coupons="coupons"
              :addedCouponValid="addedCouponValid"
              :deferrableCombos="deferrableCombos"
              :giftCardItems="giftCardItems"
              @addcoupon="addCoupon"
              @removecoupon="removeCoupon"
              @instructions="setOrderInstructions"
              @set-dirty="isActiveStageDirty = $event"
              @checkout="nextStage"
            />

            <WCBillingStage
              id="stage2"
              :billing="billing"
              :activeStage="checkoutStage"
              @set-dirty="isActiveStageDirty = $event"
              @update:valid="isActiveStageValid = $event"
              @checkout="nextStage"
              @goback="prevStage"
            />

            <WCShippingStage
              id="stage3"
              :shipping="shipping"
              :shippingMethod="shippingMethod"
              :reservationDate="reservationDate"
              :reservationTime="reservationTime"
              :lineItems="lineItems"
              :activeStage="checkoutStage"
              :giftCardItems="giftCardItems"
              @set-dirty="isActiveStageDirty = $event"
              @update:valid="isActiveStageValid = $event"
              @checkout="nextStage"
              @goback="prevStage"
            />

            <WCPaymentStage
              id="stage4"
              class="payment-stage"
              :activeStage="checkoutStage"
              :totals="totals"
              :tenders="tenders"
              :paymentMethod="defaultPaymentMethod"
              :paymentMethods="paymentMethods"
              :lineItems="lineItems"
              :giftCardItems="giftCardItems"
              @set-dirty="isActiveStageDirty = $event"
              @update:valid="isActiveStageValid = $event"
              @checkout="nextStage"
              @update:method="defaultPaymentMethod = $event"
              @tokenize="tokenizeCallback = $event"
              @pay="payWithAuthServer"
              @input="splitTenderChanged"
              @confirmcheckout="confirmCheckout"
              @goback="prevStage"
            />
          </ul>

          <PlatformRenderer>
            <template v-slot:onMobile>
              <WCOrderSidebar
                v-if="checkoutStage === 3"
                :billing="billing"
                :totals="totals"
                :lineItems="lineItems"
                :shippingMethod="shippingMethod"
                :reservationDate="reservationDate"
                :reservationTime="reservationTime"
                :activeStage="checkoutStage"
                :maxStage="maxStage"
                :maxStageReached="maxStageReached"
                :isActiveStageValid="isActiveStageValid"
                @checkout="nextStage"
                @confirmcheckout="confirmCheckout"
                class="col-lg-3"
                @goback="prevStage"
              />
            </template>
            <template v-slot:onDesktop>
              <div class="col-md-3">
                <WCOrderSidebar
                  :billing="billing"
                  :totals="totals"
                  :lineItems="lineItems"
                  :shippingMethod="shippingMethod"
                  :reservationDate="reservationDate"
                  :reservationTime="reservationTime"
                  :activeStage="checkoutStage"
                  :maxStage="maxStage"
                  :maxStageReached="maxStageReached"
                  :isActiveStageValid="isActiveStageValid"
                  @checkout="nextStage"
                  @confirmcheckout="confirmCheckout"
                />
              </div>
            </template>
          </PlatformRenderer>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import PlatformRenderer from '@/modules/platform/components/PlatformRenderer';
import axios from 'axios';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCEmptyCart from '@/modules/cart/components/WCEmptyCart/WCEmptyCart.vue';
import AlertService from '@/modules/alerts/services/AlertService';
import WCOrderInProgressAlert from '@/modules/orders/components/WCOrderInProgressAlert/WCOrderInProgressAlert.vue';
import FocusMixin from '@/mixins/FocusMixin';
import UserMixin from '@/modules/user/mixins/UserMixin';
import TrackableEventConstants from '@/constants/TrackableEventConstants';
import AnalyticsService from '@/services/analytics.service';
import WCFinalizeStage from '../../components/WCFinalizeStage/WCFinalizeStage.vue';
import WCBillingStage from '../../components/WCBillingStage/WCBillingStage.vue';
import WCShippingStage from '../../components/WCShippingStage/WCShippingStage.vue';
import WCPaymentStage from '../../components/WCPaymentStage/WCPaymentStage.vue';
import WCOrderSidebar from '../../components/WCOrderSidebar/WCOrderSidebar.vue';

export default {
  components: {
    WCSpinner,
    WCEmptyCart,
    WCOrderSidebar,
    WCFinalizeStage,
    WCBillingStage,
    WCShippingStage,
    WCPaymentStage,
    PlatformRenderer,
  },
  data() {
    return {
      isActiveStageDirty: false,
      isActiveStageValid: true,
      defaultPaymentMethod: null,
      tokenizeCallback: null,
      isLoading: false,
      form: null,
      amount: 0,
      splitTenderMethod: '',
    };
  },
  mixins: [FocusMixin, UserMixin], // Mixin for main content focus via routing
  async created() {
    AnalyticsService.track(TrackableEventConstants.CheckoutStarted, { lineItems: this.lineItems });
    this.syncCart();
  },
  methods: {
    ...mapActions({
      checkout: 'cart/checkout',
      checkoutModified: 'cart/checkoutModified',
      getUser: 'user/getUser',
      clearCartAndCheckout: 'cart/clearCartAndCheckout',
      addCoupon: 'cart/addCoupon',
      removeCoupon: 'cart/removeCoupon',
      sync: 'cart/sync',
      clearOutOfStockItems: 'cart/clearOutOfStockItems',
      setLoadingStatus: 'cart/setLoadingStatus',
    }),
    ...mapMutations({
      setOrderInstructions: 'cart/SET_ORDER_INSTRUCTIONS',
    }),
    /**
     * Method to get cart sync up
     */
    async syncCart() {
      this.isLoading = true;
      await this.sync({ immediate: true });
      this.isLoading = false;
    },
    nextStage(newStage = this.checkoutStage + 1) {
      this.checkout({ newStage, modified: this.isActiveStageDirty });
      this.isActiveStageDirty = false;
      this.isActiveStageValid = true;
      if (newStage < 4) {
        setTimeout(() => {
          const stageElement = document.getElementsByClassName(`wc-stage__header-active`)[0];
          const offset = -200; // Used to scroll focus past the header
          const y = stageElement.getBoundingClientRect().top + window.pageYOffset + offset;
          window.scrollTo({
            top: y,
            behavior: 'smooth',
            inline: 'nearest',
          });
        }, 500);
      }
    },
    prevStage() {
      this.nextStage(this.checkoutStage - 1);
    },
    /**
     * Method to set data when split tender data changes
     * @param {Number} - Amount emitted with event
     * @param {String} - Split tender payment method
     */
    splitTenderChanged(amount, selectedPayment) {
      this.amount = amount;
      this.splitTenderMethod = selectedPayment;

      if (amount === 0 || this.splitTenderMethod === 'payTotal') {
        this.amount = this.totals.due;
      }
    },
    async confirmCheckout(recaptchaKey) {
      // If user paid with giftcard or rewards, don't make them pay again
      if (this.totals.due !== 0 && !this.payLater) {
        await this.payWithAuthServer({ method: this.defaultPaymentMethod }, recaptchaKey);
      }
      this.isLoading = true;
      try {
        const response = await axios.post('api/cart/confirm', {});

        if (response.data.complete) {
          AnalyticsService.track(TrackableEventConstants.OrderPlaced, {
            ...response.data.goodbyeData,
            lineItems: this.lineItems,
            giftCardItems: this.giftCardItems,
            subtotal: this.totals.sub,
          });

          this.clearCartAndCheckout();
          if (!this.getUser?.home) {
            this.setUserHomeStore(this.$configuration.store.id);
          }
          this.nextStage();
          if (this.isGuest) {
            this.$router.push({
              name: 'GuestOrder',
              params: { scancode: response.data.goodbyeData.scanCode },
            });
          } else {
            this.$router.push({
              name: 'Order',
              params: { scancode: response.data.goodbyeData.scanCode },
            });
          }

          AlertService.show(WCOrderInProgressAlert, {
            props: { variant: 'success' },
            timeout: 30000,
          });

          this.clearOutOfStockItems();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    async payWithAuthServer(event, recaptcha) {
      const recaptchaKey = event.recaptcha ? event.recaptcha : recaptcha;

      this.setLoadingStatus(true);

      const { method, giftCardAccount = null } = event;

      if (
        !method.provider ||
        (method.provider && method.provider !== 'stripe' && method.provider !== 'paypal')
      ) {
        this.isLoading = true;
      }
      let form = {};

      // Build payment method specific form
      if (method.type === 23) {
        form = { ...method.token };
      } else if (method.type === 14) {
        form = { giftCardAccount };
      } else if (method.type === 2) {
        form = {};
      } else if (method.type === 24) {
        form = {};
      } else if (method.type === 4) {
        form = await this.tokenizeCallback();
        // Always send expiry to backend as MM/YY or MMYY
        if (form?.expiry?.length > 5) {
          if (form.expiry.includes('/')) {
            form.expiry = form.expiry.replace('/', '');
          }
          form.expiry = form.expiry.slice(0, 2) + form.expiry.slice(4);
        }
      }

      try {
        let amount = this.amount;

        if (amount <= 0 || amount > this.totals.due) {
          amount = this.totals.due;
        } else if (amount >= method.balance) {
          amount = method.balance;
        }

        const payload = {
          ...form,
          recaptcha: recaptchaKey,
          amount,
        };

        this.setLoadingStatus(true);

        if (method) {
          const response = await axios.post(`api/payauth/${method.methodId}/authorize`, payload);
          if (response.data.message === 'Success') {
            this.amount = this.totals.due;
          }
        }

        await this.sync({ immediate: true });
        if (this.totals.due === 0) {
          this.isActiveStageValid = true;
        }
      } catch (error) {
        console.error(error);
        const response = error.response;
        const message =
          response && response.data && response.data.message
            ? response.data.message
            : this.$t('errorInPayment');
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: this.$t('error'),
            message,
          },
          timeout: 7000,
        });
      } finally {
        this.isLoading = false;
        this.setLoadingStatus(false);
      }
    },
  },
  computed: {
    ...mapGetters({
      isSyncing: 'cart/isSyncing',
      isFetched: 'cart/isFetched',
      isEmpty: 'cart/isEmpty',
      totals: 'cart/getTotals',
      orderInstructions: 'cart/getOrderInstructions',
      billing: 'cart/getBilling',
      shipping: 'cart/getShipping',
      checkoutStage: 'cart/getCheckoutStage',
      maxStage: 'cart/getMaxStage',
      maxStageReached: 'cart/getMaxStageReached',
      coupons: 'cart/getCoupons',
      lineItems: 'cart/getLineItems',
      addedCouponValid: 'cart/getAddedCouponValid',
      reservationDate: 'cart/getReservationDate',
      reservationTime: 'cart/getReservationTime',
      shippingMethod: 'cart/getShippingMethod',
      paymentMethods: 'cart/getPaymentMethods',
      deferrableCombos: 'cart/getDeferrableCombos',
      giftCardItems: 'cart/getGiftCardItems',
      getOutOfStockLineItemsOfType: 'cart/getOutOfStockLineItemsOfType',
      isGuest: 'user/isGuest',
      payLater: 'cart/getPayLater',
    }),

    tenders() {
      const tenders = this.lineItems
        ? this.lineItems.filter(lineItem => lineItem.type === 2)
        : null;
      return tenders && tenders.length > 0 ? tenders : null;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

@include media-breakpoint-up(lg) {
  .payment-stage {
    min-height: rem(500px);
  }
}
</style>
